import { ActivityIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  IngredientId,
  SelectedTicketInfo,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';
import { trackDeserializeActivityWithoutSupplierToken } from '@libs/feature-toggle';

import {
  ACTIVITY_PROPS_SEPARATOR,
  SELECTED_TICKET_PREFIX,
  TICKET_PROPS_SEPARATOR,
  TICKET_PROPS_WRAPPER_CLOSE,
  TICKET_PROPS_WRAPPER_OPEN,
} from './constants';

export function deserializeActivity(
  value: string,
  parentIngredientId: IngredientId
): ActivityIngredient {
  const { code, offset, serializedTickets } =
    deserializeIngredientParameters(value);

  const tickets = deserializeTickets(serializedTickets);

  const subIngredient = new ActivityIngredient(code, parentIngredientId);
  subIngredient.offset = offset;
  subIngredient.tickets = tickets;

  return subIngredient;
}

function deserializeIngredientParameters(value: string): {
  code: string;
  offset: number;
  serializedTickets: string[];
} {
  const serializedActivityIngredient = value.replace(
    `${SELECTED_TICKET_PREFIX}`,
    ''
  );

  const [code, serializedOffset, ...serializedTickets] =
    serializedActivityIngredient.split(
      new RegExp(
        `${ACTIVITY_PROPS_SEPARATOR}(?![^${TICKET_PROPS_WRAPPER_OPEN}${TICKET_PROPS_WRAPPER_CLOSE}]*\\${TICKET_PROPS_WRAPPER_CLOSE})`
      )
    );

  assertValue(
    code,
    'activitySerializer.ts > ActivitySerializer > deserialize > code'
  );

  assertValue(
    serializedOffset,
    'activitySerializer.ts > ActivitySerializer > deserialize > serializedOffset'
  );

  let offset = Number(serializedOffset);

  if (isNaN(offset)) {
    offset = 0;
  }

  return {
    code,
    offset,
    serializedTickets,
  };
}

function deserializeTickets(serializedTickets: string[]): SelectedTicketInfo[] {
  return serializedTickets
    .filter((serializedTicket) => serializedTicket !== '')
    .map<SelectedTicketInfo>((serializedTicket) => {
      const [rateId, ageRangeId, quantity, timeSlotId, supplierToken] =
        serializedTicket.slice(1, -1).split(TICKET_PROPS_SEPARATOR);

      assertValue(
        rateId,
        'activitySerializer.ts > ActivitySerializer > deserialize > rateId'
      );
      assertValue(
        ageRangeId,
        'activitySerializer.ts > ActivitySerializer > deserialize > ageRangeId'
      );
      assertValue(
        quantity,
        'activitySerializer.ts > ActivitySerializer > deserialize > quantity'
      );
      assertValue(
        timeSlotId,
        'activitySerializer.ts > ActivitySerializer > deserialize > timeSlotId'
      );

      if (supplierToken === undefined) {
        trackDeserializeActivityWithoutSupplierToken();
      }

      return {
        ageRangeId,
        quantity: Number(quantity),
        rate: {
          id: rateId,
        },
        timeSlot: timeSlotId.length
          ? {
              id: decodeURIComponent(timeSlotId),
            }
          : undefined,
        supplierToken: supplierToken?.length ? supplierToken : undefined,
      };
    });
}
